// API Service for Bus Dashboard Application

const API_BASE_URL = "https://bus-dashboard-api.phuket.cloud/api";
// const API_BASE_URL = "http://localhost:3001/api";

/**
 * Map internal form data to API format
 */
const mapToApiFormat = (formData) => {
  // console.log("📝 mapToApiFormat - Input:", formData);

  // Handle bus_stops - support both old and new field names
  let busStops = [];
  if (formData.bus_stops && Array.isArray(formData.bus_stops)) {
    busStops = formData.bus_stops;
  } else if (formData.busStops && Array.isArray(formData.busStops)) {
    busStops = formData.busStops.map((stop) => ({
      stop_name: stop.name,
      passengers: stop.passengers,
    }));
  }

  const result = {
    date: formData.date,
    bus_line: formData.bus_line || formData.route,
    bus: formData.bus || formData.trip,
    license_plate: formData.license_plate || formData.license,
    driver_name: formData.driver_name || formData.driver,
    tickets: formData.tickets,
    daypass: formData.daypass || formData.monthlyPass,
    daypass_sold: formData.daypass_sold || 0,
    daypass_income: formData.daypass_income || 0,
    trips: formData.trips || formData.tripCount,
    bus_stops: busStops,
  };

  // console.log("📤 mapToApiFormat - Output:", result);
  return result;
};

/**
 * Map API response to internal format
 */
const mapFromApiFormat = (apiData) => {
  // Parse bus_stops if it's a string
  let busStops = [];
  if (typeof apiData.bus_stops === "string") {
    try {
      busStops = JSON.parse(apiData.bus_stops);
    } catch (e) {
      console.error("Error parsing bus_stops:", e);
      busStops = [];
    }
  } else if (Array.isArray(apiData.bus_stops)) {
    busStops = apiData.bus_stops;
  }

  return {
    id: apiData.id || Date.now(),
    date: apiData.date,
    route: apiData.bus_line,
    trip: apiData.bus,
    license: apiData.license_plate,
    driver: apiData.driver_name,
    tickets: apiData.tickets,
    monthlyPass: apiData.daypass,
    daypass_income: apiData.daypass_income || 0,
    tripCount: apiData.trips,
    busStops: busStops.map((stop) => ({
      name: stop.stop_name,
      passengers: stop.passengers,
    })),
  };
};

/**
 * Create a new passenger origin record
 */
export const createPassengerOrigin = async (formData) => {
  try {
    const apiData = mapToApiFormat(formData);

    const response = await fetch(`${API_BASE_URL}/passenger-origins`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(apiData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();

    // Show success notification
    // console.log("✅ บันทึกข้อมูลสำเร็จ:", result);

    return {
      success: true,
      data: mapFromApiFormat(result.data || result),
      message: "✅ บันทึกข้อมูลเรียบร้อยแล้ว",
    };
  } catch (error) {
    console.error("Error creating passenger origin:", error);
    return {
      success: false,
      error: error.message,
      message: "เกิดข้อผิดพลาดในการบันทึกข้อมูล",
    };
  }
};

/**
 * Update an existing passenger origin record
 */
export const updatePassengerOrigin = async (id, formData) => {
  try {
    const apiData = mapToApiFormat(formData);

    const response = await fetch(`${API_BASE_URL}/passenger-origins/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(apiData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();

    // Show success notification
    // console.log("✅ อัปเดตข้อมูลสำเร็จ:", result);

    return {
      success: true,
      data: mapFromApiFormat(result.data || result),
      message: "✅ อัปเดตข้อมูลเรียบร้อยแล้ว",
    };
  } catch (error) {
    console.error("Error updating passenger origin:", error);
    return {
      success: false,
      error: error.message,
      message: "เกิดข้อผิดพลาดในการอัปเดตข้อมูล",
    };
  }
};

/**
 * Delete a passenger origin record
 */
export const deletePassengerOrigin = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/passenger-origins/${id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    return {
      success: true,
      message: "✅ ลบข้อมูลเรียบร้อยแล้ว",
    };
  } catch (error) {
    console.error("Error deleting passenger origin:", error);
    return {
      success: false,
      error: error.message,
      message: "เกิดข้อผิดพลาดในการลบข้อมูล",
    };
  }
};

/**
 * Get all passenger origin records
 */
export const getPassengerOrigins = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/passenger-origins`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      success: true,
      data: Array.isArray(data) ? data.map(mapFromApiFormat) : [],
      message: "ดึงข้อมูลสำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching passenger origins:", error);
    return {
      success: false,
      error: error.message,
      data: [],
      message: "เกิดข้อผิดพลาดในการดึงข้อมูล",
    };
  }
};

/**
 * Get statistics summary
 */
export const getStatisticsSummary = async (startDate, endDate) => {
  try {
    let url = `${API_BASE_URL}/statistics/summary`;

    // Add query parameters if provided
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    const data = result.data || result;

    // Calculate bus stop passengers: total - tickets - monthly pass
    const totalTickets = data.total_tickets || 0;
    const totalMonthlyPass = data.total_daypass || 0;
    const totalPassengers = data.total_passengers || 0;
    const totalBusStopPassengers = Math.max(
      0,
      totalPassengers - totalTickets - totalMonthlyPass,
    );

    return {
      success: true,
      data: {
        totalTickets: totalTickets,
        totalMonthlyPass: totalMonthlyPass,
        totalBusStopPassengers: totalBusStopPassengers,
        totalTrips: data.total_trips || 0,
        totalCustomers: totalTickets + totalMonthlyPass,
        totalDaypassIncome: data.total_daypass_income || 0,
      },
      message: "ดึงข้อมูลสรุปสำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching statistics summary:", error);
    return {
      success: false,
      error: error.message,
      data: null,
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลสรุป",
    };
  }
};

/**
 * Get advanced statistics
 */
export const getStatisticsAdvanced = async (
  startDate,
  endDate,
  busLine,
  year,
) => {
  try {
    let url = `${API_BASE_URL}/statistics/advanced`;

    // Add query parameters if provided
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (busLine) params.append("bus_line", busLine);
    if (year) params.append("year", year);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      success: true,
      data: data,
      message: "ดึงข้อมูลวิเคราะห์สำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching advanced statistics:", error);
    return {
      success: false,
      error: error.message,
      data: null,
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลวิเคราะห์",
    };
  }
};

/**
 * Get daily analysis statistics
 */
export const getDailyAnalysis = async (startDate, endDate, busLine, year) => {
  try {
    let url = `${API_BASE_URL}/statistics/daily-analysis`;

    // Add query parameters if provided
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (busLine) params.append("bus_line", busLine);
    if (year) params.append("year", year);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      success: true,
      data: data,
      message: "ดึงข้อมูลวิเคราะห์รายวันสำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching daily analysis:", error);
    return {
      success: false,
      error: error.message,
      data: null,
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลวิเคราะห์รายวัน",
    };
  }
};

/**
 * Get bus line analysis statistics
 */
export const getBusLineAnalysis = async (startDate, endDate, busLine, year) => {
  try {
    let url = `${API_BASE_URL}/statistics/bus-line-analysis`;

    // Add query parameters if provided
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (busLine) params.append("bus_line", busLine);
    if (year) params.append("year", year);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      success: true,
      data: data,
      message: "ดึงข้อมูลวิเคราะห์รายสายสำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching bus line analysis:", error);
    return {
      success: false,
      error: error.message,
      data: null,
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลวิเคราะห์รายสาย",
    };
  }
};

/**
 * Get timeline chart data
 */
export const getTimelineChart = async (startDate, endDate, busLine, year) => {
  try {
    let url = `${API_BASE_URL}/statistics/timeline-chart`;

    // Add query parameters if provided
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (busLine) params.append("bus_line", busLine);
    if (year) params.append("year", year);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      success: true,
      data: data,
      message: "ดึงข้อมูลกราฟไทม์ไลน์สำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching timeline chart:", error);
    return {
      success: false,
      error: error.message,
      data: null,
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลกราฟไทม์ไลน์",
    };
  }
};

/**
 * Get day of week chart data
 */
export const getDayOfWeekChart = async (startDate, endDate, busLine, year) => {
  try {
    let url = `${API_BASE_URL}/statistics/day-of-week-chart`;

    // Add query parameters if provided
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (busLine) params.append("bus_line", busLine);
    if (year) params.append("year", year);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      success: true,
      data: data,
      message: "ดึงข้อมูลกราฟวันในสัปดาห์สำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching day of week chart:", error);
    return {
      success: false,
      error: error.message,
      data: null,
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลกราฟวันในสัปดาห์",
    };
  }
};

/**
 * Get seat utilization chart data
 */
export const getSeatUtilizationChart = async (
  startDate,
  endDate,
  busLine,
  year,
) => {
  try {
    let url = `${API_BASE_URL}/statistics/seat-utilization-chart`;

    // Add query parameters if provided
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (busLine) params.append("bus_line", busLine);
    if (year) params.append("year", year);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      success: true,
      data: data,
      message: "ดึงข้อมูลกราฟการใช้ที่นั่งสำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching seat utilization chart:", error);
    return {
      success: false,
      error: error.message,
      data: null,
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลกราฟการใช้ที่นั่ง",
    };
  }
};

/**
 * Get weekday statistics
 */
export const getWeekdayStats = async (startDate, endDate, busLine, year) => {
  try {
    let url = `${API_BASE_URL}/statistics/weekday-stats`;

    // Add query parameters if provided
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (busLine) params.append("bus_line", busLine);
    if (year) params.append("year", year);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      success: true,
      data: data,
      message: "ดึงข้อมูลสถิติตามวันในสัปดาห์สำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching weekday stats:", error);
    return {
      success: false,
      error: error.message,
      data: null,
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลสถิติตามวันในสัปดาห์",
    };
  }
};

/**
 * Get all fuel records
 */
export const getFuelRecords = async (
  startDate,
  endDate,
  licensePlate,
  page = 1,
  limit = 10,
) => {
  try {
    let url = `${API_BASE_URL}/fuel-records`;

    // Add query parameters if provided
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (licensePlate) params.append("license_plate", licensePlate);
    if (page) params.append("page", page);
    if (limit) params.append("limit", limit);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();

    // Support both paginated and non-paginated responses
    if (result.data && result.pagination) {
      return {
        success: true,
        data: Array.isArray(result.data) ? result.data : [],
        pagination: result.pagination,
        message: "ดึงข้อมูลการเติมน้ำมันสำเร็จ",
      };
    }

    const data = result.data || result;
    return {
      success: true,
      data: Array.isArray(data) ? data : [],
      message: "ดึงข้อมูลการเติมน้ำมันสำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching fuel records:", error);
    return {
      success: false,
      error: error.message,
      data: [],
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลการเติมน้ำมัน",
    };
  }
};

/**
 * Create a new fuel record
 */
export const createFuelRecord = async (fuelData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/fuel-records`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fuelData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();

    return {
      success: true,
      data: result.data || result,
      message: "✅ บันทึกข้อมูลการเติมน้ำมันเรียบร้อยแล้ว",
    };
  } catch (error) {
    console.error("Error creating fuel record:", error);
    return {
      success: false,
      error: error.message,
      message: "เกิดข้อผิดพลาดในการบันทึกข้อมูลการเติมน้ำมัน",
    };
  }
};

/**
 * Update a fuel record
 */
export const updateFuelRecord = async (id, fuelData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/fuel-records/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fuelData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();

    return {
      success: true,
      data: result.data || result,
      message: "✅ อัปเดตข้อมูลการเติมน้ำมันเรียบร้อยแล้ว",
    };
  } catch (error) {
    console.error("Error updating fuel record:", error);
    return {
      success: false,
      error: error.message,
      message: "เกิดข้อผิดพลาดในการอัปเดตข้อมูลการเติมน้ำมัน",
    };
  }
};

/**
 * Delete a fuel record
 */
export const deleteFuelRecord = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/fuel-records/${id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    return {
      success: true,
      message: "✅ ลบข้อมูลการเติมน้ำมันเรียบร้อยแล้ว",
    };
  } catch (error) {
    console.error("Error deleting fuel record:", error);
    return {
      success: false,
      error: error.message,
      message: "เกิดข้อผิดพลาดในการลบข้อมูลการเติมน้ำมัน",
    };
  }
};

/**
 * Get fuel statistics
 */
export const getFuelStatistics = async (startDate, endDate, licensePlate) => {
  try {
    let url = `${API_BASE_URL}/fuel-records/statistics`;

    // Add query parameters if provided
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (licensePlate) params.append("license_plate", licensePlate);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      success: true,
      data: data,
      message: "ดึงข้อมูลสถิติการเติมน้ำมันสำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching fuel statistics:", error);
    return {
      success: false,
      error: error.message,
      data: null,
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลสถิติการเติมน้ำมัน",
    };
  }
};

// Get fuel records grouped by gas station
export const getFuelRecordsByGasStation = async (
  startDate,
  endDate,
  licensePlate,
) => {
  try {
    let url = `${API_BASE_URL}/fuel-records/by-gas-station`;

    // Add query parameters if provided
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (licensePlate) params.append("license_plate", licensePlate);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      success: true,
      data: data,
      message: "ดึงข้อมูลตามปั๊มน้ำมันสำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching fuel records by gas station:", error);
    return {
      success: false,
      error: error.message,
      data: [],
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลตามปั๊มน้ำมัน",
    };
  }
};

// Get fuel records daily chart data
export const getFuelDailyChart = async (startDate, endDate, licensePlate) => {
  try {
    let url = `${API_BASE_URL}/fuel-records/daily-chart`;

    // Add query parameters if provided
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (licensePlate) params.append("license_plate", licensePlate);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      success: true,
      data: data,
      message: "ดึงข้อมูลกราฟรายวันสำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching fuel daily chart:", error);
    return {
      success: false,
      error: error.message,
      data: [],
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลกราฟรายวัน",
    };
  }
};

// Get fuel records by vehicle
export const getFuelRecordsByVehicle = async (
  startDate,
  endDate,
  licensePlate,
) => {
  try {
    let url = `${API_BASE_URL}/fuel-records/by-vehicle`;

    // Add query parameters if provided
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (licensePlate) params.append("license_plate", licensePlate);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    const data = result.data || result;

    return {
      success: true,
      data: data,
      message: "ดึงข้อมูลตามรถสำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching fuel records by vehicle:", error);
    return {
      success: false,
      error: error.message,
      data: [],
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลตามรถ",
    };
  }
};

/**
 * Get vehicles (license plates) from inventory API
 */
export const getVehicles = async () => {
  try {
    const INVENTORY_API_URL = "https://inventory.phuket.cloud/api/models-car/";
    const BEARER_TOKEN =
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ0b2tlbl90eXBlIjoiYWNjZXNzIiwiZXhwIjoyMzYzNTQyNzM4LCJpYXQiOjE3NjM1NDI3OTgsImp0aSI6ImQ1ODUwYWEzMTU5YjQwMDBiY2M0ZjhjNzQyMTExNzk2IiwidXNlcl9pZCI6MSwidXNlcm5hbWUiOiJ1c2VyLTEiLCJyb2xlIjoiYWRtaW4ifQ.9b-d0Zk-la6pG8tUBUksSeVRj5JRjUIXo3_TVVb8CAY";

    const response = await fetch(INVENTORY_API_URL, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${BEARER_TOKEN}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    const data = result || [];

    // Transform data to format needed for SearchableSelect
    const vehicles = data.map((vehicle) => ({
      value: vehicle.license_plate,
      label: vehicle.license_plate,
      id: vehicle.id,
      device_id: vehicle.device_id,
      model: vehicle.model,
      miles: vehicle.miles,
    }));

    return {
      success: true,
      data: vehicles,
      message: "ดึงข้อมูลป้ายทะเบียนสำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching vehicles:", error);
    return {
      success: false,
      error: error.message,
      data: [],
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลป้ายทะเบียน",
    };
  }
};

/**
 * Get drivers (members) from inventory API
 */
export const getDrivers = async () => {
  try {
    const INVENTORY_API_URL = "https://inventory.phuket.cloud/api/members/";
    const BEARER_TOKEN =
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ0b2tlbl90eXBlIjoiYWNjZXNzIiwiZXhwIjoyMzYzNTQyNzM4LCJpYXQiOjE3NjM1NDI3OTgsImp0aSI6ImQ1ODUwYWEzMTU5YjQwMDBiY2M0ZjhjNzQyMTExNzk2IiwidXNlcl9pZCI6MSwidXNlcm5hbWUiOiJ1c2VyLTEiLCJyb2xlIjoiYWRtaW4ifQ.9b-d0Zk-la6pG8tUBUksSeVRj5JRjUIXo3_TVVb8CAY";

    const response = await fetch(INVENTORY_API_URL, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${BEARER_TOKEN}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const result = await response.json();
    const data = result || [];

    // Transform data to format needed for SearchableSelect
    // Display format: "ชื่อ นามสกุล (ชื่อเล่น)"
    const drivers = data.map((member) => {
      const fullName = `${member.first_name} ${member.last_name}`;
      const displayName = member.nick_name
        ? `${fullName} (${member.nick_name})`
        : fullName;

      return {
        value: fullName,
        label: displayName,
        id: member.id,
        firstName: member.first_name,
        lastName: member.last_name,
        nickName: member.nick_name,
        tell: member.tell,
      };
    });

    return {
      success: true,
      data: drivers,
      message: "ดึงข้อมูลคนขับสำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching drivers:", error);
    return {
      success: false,
      error: error.message,
      data: [],
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลคนขับ",
    };
  }
};

/**
 * Fetch bus stop names from API
 */
export const getBusStops = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/bus-stops`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || "Failed to fetch bus stops");
    }

    return {
      success: true,
      data: result.data,
      message: "ดึงข้อมูลป้ายรถเมล์สำเร็จ",
    };
  } catch (error) {
    console.error("Error fetching bus stops:", error);
    return {
      success: false,
      error: error.message,
      data: [],
      message: "เกิดข้อผิดพลาดในการดึงข้อมูลป้ายรถเมล์",
    };
  }
};
